import { HttpError } from "../../shared/types/exception";
import { getExceptionStatusAndMessage } from "./sse.utils";
import { EventMessage, sseParseMessages } from "./sse.parser";
import { sleep } from "./utils";

const SSE_INITIAL_RETRY_TIMER_TIMEOUT = 60 * 1000;
const SSE_MIN_RETRY_TIMER_TIMEOUT = 10 * 1000;
const SSE_RECONNECT_HTTP_CALL_DELAY = 10 * 1000;

const logger = {
  error: console.error,
  warn: console.warn,
  info: console.info,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  log: (..._data: any[]) => {},
};

// Custom parser for SSE due to missing header functionality in browser EventSource API. Instead
// of this class npm package "eventsource" can be used.
// https://stackoverflow.com/questions/4361173/http-headers-in-websockets-client-api
// https://stackoverflow.com/questions/28176933/http-authorization-header-in-eventsource-server-sent-events
export class ServerSentEvents {
  private isOpen = true;
  private response: Response | null = null;
  private reader: ReadableStreamDefaultReader<string> | undefined;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    url: string,
    headers: HeadersInit | (() => Promise<HeadersInit>),
    private queryParams: string[]
  ) {
    logger.log(`SseNotifications: Constructor, `, queryParams.join("&"));
    this.open(url, headers);
  }

  private setRetryTimer(retry: number) {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
    }
    this.retryTimer = setTimeout(() => {
      logger.log(`SseNotifications: Retry timeout`);
      try {
        this.retryTimer = null;
        if (this.isOpen) {
          this.setRetryTimer(
            SSE_MIN_RETRY_TIMER_TIMEOUT < retry
              ? retry
              : SSE_MIN_RETRY_TIMER_TIMEOUT
          );
        }
        this.reader?.cancel();
      } catch (exception) {
        logger.info(
          `SseNotifications: Exception when setting timer, ${exception instanceof Error ? exception.toString() : "unknown"}`
        );
      }
    }, retry);
  }

  private async readBody() {
    logger.log(`SseNotifications: readBody`);
    let done = false;
    while (this.reader && this.isOpen && !done) {
      const result = await this.reader.read();
      const value = result.value;
      if (value) {
        if (this.onmessage) {
          let retry = SSE_INITIAL_RETRY_TIMER_TIMEOUT;
          const messages = sseParseMessages(value);
          logger.log(
            `SseNotifications: Parser messages: \n${messages.map((message) => JSON.stringify(message)).join("\n")}\n`
          );
          for (const message of messages) {
            logger.log(`SseNotifications: readBody calling onmessage`);
            this.onmessage(message);
            if (message.retry && message.retry < retry) {
              retry = message.retry;
            }
          }
          this.setRetryTimer(retry);
        }
      }
      done = result.done;
    }
    if (this.reader?.cancel) {
      this.reader.cancel();
    }
    logger.log(`SseNotifications: readBody exit`);
  }

  private async open(
    url: string,
    headers: HeadersInit | (() => Promise<HeadersInit>)
  ) {
    logger.log(`SseNotifications: Open`);
    this.setRetryTimer(SSE_INITIAL_RETRY_TIMER_TIMEOUT);
    while (this.isOpen) {
      try {
        const request = new Request(
          this.queryParams.length
            ? `${url}?${this.queryParams.join("&")}`
            : url,
          {
            method: "GET",
            headers: typeof headers === "function" ? await headers() : headers,
          }
        );
        this.response = await fetch(request);
        if (!this.response.ok) {
          throw new HttpError(this.response.status, this.response.statusText);
        }

        this.reader = this.response.body
          ?.pipeThrough(new TextDecoderStream())
          .getReader();

        await this.readBody();
      } catch (exception) {
        const { status, message } = getExceptionStatusAndMessage(exception);
        logger.info(
          "SseNotifications: SSE socket unexpectedly closed, ",
          message,
          status ?? ""
        );
        if (this.onerror) {
          this.onerror();
        }
      }
      if (this.isOpen) {
        await sleep(SSE_RECONNECT_HTTP_CALL_DELAY);
      }
    }

    if (!this.isOpen && this.onclose) {
      this.onclose();
    }
    logger.log(`SseNotifications: Open exit`, this.isOpen);
  }

  onmessage: null | ((message: EventMessage) => void) = null;

  onerror: null | (() => void) = null;

  onclose: null | (() => void) = null;

  close() {
    logger.log(`SseNotifications: Close`);
    try {
      if (this.isOpen) {
        this.isOpen = false;
        if (this.retryTimer) {
          clearTimeout(this.retryTimer);
          this.retryTimer = null;
        }
        if (this.reader?.cancel) {
          this.reader.cancel();
        }
      } else {
        if (this.onclose) {
          this.onclose();
        }
      }
    } catch (exception) {
      logger.log(
        "Exception in close, ",
        exception instanceof Error ? exception.toString() : "unknown"
      );
    }
  }

  setQueryParams(queryParams: string[]) {
    // The time set here may not be applied directly but next time a new fetch request is done.
    // The time is used to send all new notifications in reconnect so this should be ok.
    logger.log(`SseNotifications: setQueryParams`, queryParams.join("&"));
    this.queryParams = queryParams;
  }
}
